package dev.youndie.site.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initBooblikPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("booblik.md", FrontMatterElement.Builder { addScalar("title", "booblik"); addScalar("searchTitle", "Message broker in Kotlin on an append-only log — zero-copy, no coordinator"); addScalar("description", "A broker that fits in one head and one process: topics into partitions, partitions into segments, offsets the consumer keeps. No ZooKeeper, no quorum."); addScalar("library", "booblik"); addScalar("post", "/blog/a-broker-that-fits-in-one-head"); }))
}

@Page("/booblik")
@Layout(".components.layouts.LibraryLayout")
@Composable
fun BooblikPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The niche is not \"a Kafka replacement\". It is a broker that fits in one head and one process — no ZooKeeper, no KRaft quorum, no group coordinator. Everything that makes Kafka operable as a cluster is deliberately absent; everything that makes a log fast is reproduced and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("measured")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-you-get") }) {
        org.jetbrains.compose.web.dom.Text("What you get")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Segments")
            }
            org.jetbrains.compose.web.dom.Text(" with a sparse offset index, two write paths (")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("FileChannel")
            }
            org.jetbrains.compose.web.dom.Text(" and an FFM memory mapping), rolling, retention by size and age, and recovery from a torn write.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("A per-partition writer actor with no timer.")
            }
            org.jetbrains.compose.web.dom.Text(" It groups whatever is already in its mailbox into one barrier, so the batch size ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("is")
            }
            org.jetbrains.compose.web.dom.Text(" the load, measured at the instant before the write. No configured window means no latency floor to retune.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Zero-copy FETCH")
            }
            org.jetbrains.compose.web.dom.Text(" straight from the page cache to the socket with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("transferTo")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Long FETCH")
            }
            org.jetbrains.compose.web.dom.Text(" — a caught-up consumer waits on the broker instead of polling, and a write during the wait wakes it immediately.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A client library: pipelined connections matched by correlation id, an accumulating producer, key-based routing, and subscriptions as ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Flow<RecordBatch>")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-is-deliberately-missing") }) {
        org.jetbrains.compose.web.dom.Text("What is deliberately missing")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("TLS and compression.")
        }
        org.jetbrains.compose.web.dom.Text(" Both require transforming the bytes, and transforming them means copying them — they are incompatible with the zero-copy path by construction, not merely unimplemented. Per-record CRC32C is verified on every read that touches the bytes, which the zero-copy path by definition does not; saying so is more useful than a footnote claiming end-to-end verification.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Runtime topic creation is out for the same kind of reason: the partition set is fixed at startup, and that is what removes the coordinator.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("measured-not-asserted") }) {
        org.jetbrains.compose.web.dom.Text("Measured, not asserted")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Numbers come from a rented Linux machine with nothing else running and, for end-to-end figures, two such machines with a 7.85 Gbit/s link — the broker in its own process, the load generator on the other host. Methodology and full tables are in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docs/benchmarking.md")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
}
